import { jsPDF } from 'jspdf';

const hex = (c) => {
  const n = parseInt(c.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const COLORS = {
  red: hex('#7A1F25'),
  gold: hex('#B8934A'),
  goldLight: hex('#D4A94B'),
  cream: hex('#FAF6F0'),
  dark: hex('#1F2A24'),
  muted: hex('#6B6257'),
  border: hex('#E8DFCF')
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const TEXT = {
  fi: {
    title: 'VIIKON LOUNAS',
    week: 'Viikko',
    subtitle: 'Päivittäin vaihtuva lounasbuffet tuoreista raaka-aineista',
    served: 'Tarjolla Ma–Pe klo 10–16',
    days: { monday: 'MAANANTAI', tuesday: 'TIISTAI', wednesday: 'KESKIVIIKKO', thursday: 'TORSTAI', friday: 'PERJANTAI' },
    address: 'Kalustetie 1, 01720 Vantaa',
    phone: '040 7611347',
    hours: 'Ma–Pe 8–18  ·  La–Su Suljettu',
    file: 'Ravintola-La-Maria-Lounas'
  },
  en: {
    title: "WEEK'S LUNCH",
    week: 'Week',
    subtitle: 'Daily changing lunch buffet from fresh ingredients',
    served: 'Served Mon–Fri 10am–4pm',
    days: { monday: 'MONDAY', tuesday: 'TUESDAY', wednesday: 'WEDNESDAY', thursday: 'THURSDAY', friday: 'FRIDAY' },
    address: 'Kalustetie 1, 01720 Vantaa',
    phone: '040 7611347',
    hours: 'Mon–Fri 8am–6pm  ·  Sat–Sun Closed',
    file: 'Restaurant-La-Maria-Lunch'
  }
};

const safe = (s) => (s || '').toString();
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const generateLunchMenuPDF = ({ menu, week, language = 'fi' }) => {
  const T = TEXT[language] || TEXT.fi;
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = 210;
  const pageH = 297;
  const margin = 14;

  // ---- Count dishes & decide tight-mode params ----
  const dishesPerDay = DAY_ORDER.map(d => (menu?.[d]?.dishes?.length || 0));
  const effDishes = dishesPerDay.reduce((s, n) => s + Math.max(1, n), 0); // empty day counts as 1 row "—"
  const realTotal = dishesPerDay.reduce((s, n) => s + n, 0);

  // ---- Adaptive section sizes ----
  const veryCrowded = effDishes >= 35;
  const crowded = effDishes >= 25;

  const headerBandH = veryCrowded ? 20 : (crowded ? 23 : 26);
  const footerBandH = veryCrowded ? 11 : (crowded ? 12 : 14);
  const titleSize = veryCrowded ? 16 : (crowded ? 18 : 20);
  const restNameSize = veryCrowded ? 13 : (crowded ? 14 : 16);

  // ---- Background ----
  doc.setFillColor(...COLORS.cream);
  doc.rect(0, 0, pageW, pageH, 'F');

  // ---- Top dark-red header band ----
  doc.setFillColor(...COLORS.red);
  doc.rect(0, 0, pageW, headerBandH, 'F');
  doc.setFillColor(...COLORS.gold);
  doc.rect(0, headerBandH, pageW, 0.9, 'F');

  doc.setTextColor(...COLORS.cream);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(restNameSize);
  doc.text('RAVINTOLA LA MARIA', pageW / 2, headerBandH / 2 - 0.5, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.goldLight);
  doc.text(`${T.address}   ·   ${T.phone}`, pageW / 2, headerBandH - 5, { align: 'center' });

  // ---- Title section (compact when crowded) ----
  let y = headerBandH + (veryCrowded ? 7 : 10);
  doc.setTextColor(...COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(titleSize);
  doc.text(T.title, pageW / 2, y, { align: 'center' });

  y += veryCrowded ? 5 : 7;
  doc.setTextColor(...COLORS.red);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(crowded ? 11 : 12);
  doc.text(`${T.week} ${week || ''}`.trim(), pageW / 2, y, { align: 'center' });

  if (!veryCrowded) {
    y += 2.5;
    doc.setDrawColor(...COLORS.gold);
    doc.setLineWidth(0.4);
    doc.line(pageW / 2 - 25, y, pageW / 2 + 25, y);
  }

  if (!crowded) {
    y += 4.5;
    doc.setTextColor(...COLORS.muted);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.text(T.subtitle, pageW / 2, y, { align: 'center' });
    y += 4;
  } else {
    y += 3;
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.gold);
  doc.text(T.served.toUpperCase(), pageW / 2, y, { align: 'center' });

  // ---- Days section ----
  y += veryCrowded ? 4 : 6;
  const contentX = margin;
  const contentW = pageW - margin * 2;
  const contentBottom = pageH - footerBandH - 3;

  // Compute dish row height to fit
  const D = DAY_ORDER.length; // 5
  const dayHeaderBarH = veryCrowded ? 5 : (crowded ? 5.5 : 6);
  const afterBarGap = veryCrowded ? 3 : (crowded ? 3.2 : 4); // gap from bar bottom to first dish baseline
  const fixedPerDay = dayHeaderBarH + afterBarGap;
  const gapBetweenDays = veryCrowded ? 0.5 : (crowded ? 1 : 1.5);

  const available = contentBottom - y;
  const fixedTotal = D * fixedPerDay + (D - 1) * gapBetweenDays;
  let rowH = (available - fixedTotal) / effDishes;
  rowH = clamp(rowH, 3.2, 7);

  // Adaptive fonts based on rowH
  let fontName, fontDesc, bulletR;
  if (rowH >= 5.8) { fontName = 9.5; fontDesc = 8.2; bulletR = 1.0; }
  else if (rowH >= 4.8) { fontName = 8.8; fontDesc = 7.5; bulletR = 0.85; }
  else if (rowH >= 4.0) { fontName = 8; fontDesc = 7; bulletR = 0.75; }
  else { fontName = 7.2; fontDesc = 6.4; bulletR = 0.65; }

  const showDescription = rowH >= 3.5; // skip descriptions if extremely tight

  DAY_ORDER.forEach((dayKey, dayIdx) => {
    const dishes = menu?.[dayKey]?.dishes || [];

    // Day header bar
    doc.setFillColor(...COLORS.red);
    doc.rect(contentX, y, contentW, dayHeaderBarH, 'F');
    doc.setTextColor(...COLORS.cream);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(crowded ? 8.6 : 9.5);
    doc.text(safe(T.days[dayKey]), contentX + 4, y + dayHeaderBarH - 1.6);

    doc.setFillColor(...COLORS.goldLight);
    doc.circle(contentX + contentW - 4, y + dayHeaderBarH / 2, Math.min(1.2, dayHeaderBarH / 4), 'F');

    y += dayHeaderBarH + afterBarGap;

    const rows = dishes.length === 0 ? [{ empty: true }] : dishes;

    rows.forEach((dish) => {
      if (dish.empty) {
        doc.setTextColor(...COLORS.muted);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(fontDesc);
        doc.text('—', contentX + 5.5, y);
        y += rowH;
        return;
      }
      const content = dish?.[language] || dish?.fi || {};
      const name = safe(content.name);
      const description = safe(content.description);

      // Gold bullet
      doc.setFillColor(...COLORS.gold);
      doc.circle(contentX + 2.5, y - (rowH * 0.28), bulletR, 'F');

      // Name
      doc.setTextColor(...COLORS.dark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontName);
      const namePrint = name || '—';
      doc.text(namePrint, contentX + 5.5, y);
      const nameW = doc.getTextWidth(namePrint);

      // Description on same line (truncated if needed)
      if (showDescription && description) {
        doc.setTextColor(...COLORS.gold);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fontDesc);
        doc.text(' · ', contentX + 5.5 + nameW, y);
        const dashW = doc.getTextWidth(' · ');

        doc.setTextColor(...COLORS.muted);
        const startX = contentX + 5.5 + nameW + dashW;
        const remainingW = contentX + contentW - startX;
        const lines = doc.splitTextToSize(description, remainingW);
        doc.text(lines[0], startX, y);
      }

      y += rowH;
    });

    if (dayIdx < D - 1) y += gapBetweenDays;
  });

  // ---- Footer band ----
  const footerY = pageH - footerBandH;
  doc.setFillColor(...COLORS.red);
  doc.rect(0, footerY, pageW, footerBandH, 'F');
  doc.setTextColor(...COLORS.cream);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.2);
  doc.text(T.address, pageW / 2, footerY + footerBandH / 2 - 1, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.goldLight);
  doc.setFontSize(7.2);
  doc.text(T.phone + '   ·   ' + T.hours, pageW / 2, footerY + footerBandH - 3, { align: 'center' });

  const wkLabel = week ? `-${T.week}-${week}` : '';
  doc.save(`${T.file}${wkLabel}.pdf`);
};
